import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

const FORM_URL = 'https://readdy.ai/api/form/d6vqg3t1l1ubpb129jeg';

const CONTACT_INFO_STATIC = [
  { icon: 'ri-phone-line', infoKey: 'info1', value: '470-222-5777', href: 'tel:4702225777' },
  { icon: 'ri-mail-line', infoKey: 'info2', value: 'office@fmacrew.com', href: 'mailto:office@fmacrew.com' },
  {
    icon: 'ri-map-pin-line',
    infoKey: 'info3',
    value: '6500 McDonough Dr. Suite C15\nNorcross, GA 30093',
    href: 'https://maps.google.com/?q=6500+McDonough+Dr+Suite+C15+Norcross+GA+30093',
  },
  {
    icon: 'ri-time-line',
    infoKey: 'info4',
    value: null,
    href: '#',
  },
];

const HOURS_DATA = [
  { day: 'Mon', hours: '09:00 am – 05:00 pm', closed: false },
  { day: 'Tue', hours: '09:00 am – 05:00 pm', closed: false },
  { day: 'Wed', hours: '09:00 am – 05:00 pm', closed: false },
  { day: 'Thu', hours: '09:00 am – 05:00 pm', closed: false },
  { day: 'Fri', hours: '09:00 am – 05:00 pm', closed: false },
  { day: 'Sat', hours: 'Closed', closed: true },
  { day: 'Sun', hours: 'Closed', closed: true },
];

const GUARANTEE_STATIC = [
  { gKey: 'g1', icon: 'ri-timer-flash-line', iconColor: 'text-[#05294A]', iconBg: 'bg-[#B8E2E8]', badgeColor: 'bg-[#05294A]/10 text-[#05294A]' },
  { gKey: 'g2', icon: 'ri-money-dollar-circle-line', iconColor: 'text-[#7499CE]', iconBg: 'bg-[#7499CE]/20', badgeColor: 'bg-[#7499CE]/10 text-[#7499CE]' },
  { gKey: 'g3', icon: 'ri-shield-check-line', iconColor: 'text-[#7499CE]', iconBg: 'bg-[#7499CE]/20', badgeColor: 'bg-[#7499CE]/10 text-[#7499CE]' },
  { gKey: 'g4', icon: 'ri-medal-line', iconColor: 'text-[#05294A]', iconBg: 'bg-[#B8E2E8]', badgeColor: 'bg-[#05294A]/10 text-[#05294A]' },
];

export default function Contact() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;
    setStatus('loading');
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((val, key) => body.append(key, val as string));
    try {
      const res = await fetch(FORM_URL, { method: 'POST', body, headers: { 'Content-Type': 'application/x-www-form-urlencoded' } });
      if (!res.ok) throw new Error('Form submission failed');
      setStatus('success');
      form.reset();
      setCharCount(0);
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-16 md:py-24 relative overflow-hidden">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('https://public.readdy.ai/ai/img_res/45b9baa83c5dcf72c1d91ed5e5a35347.jpg')" }}
      />
      <div className="absolute inset-0 bg-slate-50/95" />

      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#05294A]/5 -translate-y-1/2 translate-x-1/3 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-[#7499CE]/5 translate-y-1/2 -translate-x-1/3 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        <div className="text-center mb-8 md:mb-12">
          <span className="text-[#05294A] text-xs tracking-widest uppercase font-semibold">{t('contact.label')}</span>
          <h3 className="text-3xl md:text-5xl font-bold text-slate-900 mt-4">{t('contact.heading')}</h3>
          <p className="text-slate-500 mt-4 max-w-md mx-auto text-base leading-relaxed px-4 md:px-0">{t('contact.desc')}</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-8 md:mb-12">
          {GUARANTEE_STATIC.map((g) => (
            <div key={g.gKey} className="bg-white border border-slate-200 p-4 md:p-5 flex flex-col gap-3 relative overflow-hidden group hover:border-[#92D7E8] transition-all duration-300 rounded-xl">
              <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-[#B8E2E8]/30 group-hover:bg-[#B8E2E8]/50 transition-colors duration-300 pointer-events-none" />
              <div className="flex items-center justify-between relative z-10">
                <div className={`w-10 h-10 md:w-11 md:h-11 flex items-center justify-center ${g.iconBg} ${g.iconColor}`}>
                  <i className={`${g.icon} text-lg md:text-xl`} />
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-bold ${g.badgeColor}`}>{t(`contact.${g.gKey}.badge`)}</span>
              </div>
              <div className="relative z-10">
                <p className="text-slate-900 font-bold text-sm mb-1">{t(`contact.${g.gKey}.title`)}</p>
                <p className="text-slate-500 text-xs leading-relaxed">{t(`contact.${g.gKey}.desc`)}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-10">
          <div className="lg:col-span-2 flex flex-col gap-4">
            {CONTACT_INFO_STATIC.map((item) => (
              <a
                key={item.infoKey}
                href={item.href}
                rel={item.href.startsWith('http') ? 'nofollow noopener noreferrer' : undefined}
                target={item.href.startsWith('http') ? '_blank' : undefined}
                className="flex items-start gap-4 p-4 md:p-5 bg-white border border-slate-200 hover:border-[#92D7E8] transition-all duration-300 cursor-pointer group rounded-xl"
              >
                <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center bg-[#B8E2E8] text-[#05294A] group-hover:bg-[#05294A] group-hover:text-white transition-all duration-300 shrink-0">
                  <i className={`${item.icon} text-base md:text-lg`} />
                </div>
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wide mb-1">{t(`contact.${item.infoKey}.label`)}</p>
                  {item.infoKey === 'info4' ? (
                    <div className="flex flex-col gap-1 mt-1">
                      {HOURS_DATA.map(({ day, hours, closed }) => (
                        <div key={day} className="flex items-center gap-3 text-sm">
                          <span className={`w-8 shrink-0 ${closed ? 'text-slate-500' : 'text-slate-900'}`}>{day}</span>
                          <span className={closed ? 'text-slate-500' : 'text-slate-800'}>{hours}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-800 text-sm font-medium leading-relaxed whitespace-pre-line">
                      {item.value}
                    </p>
                  )}
                </div>
              </a>
            ))}

            <div className="bg-white border border-slate-200 overflow-hidden mt-1 rounded-xl">
              <div className="px-4 md:px-5 py-4 flex items-center justify-between border-b border-slate-100">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#B8E2E8] text-[#05294A]">
                    <i className="ri-map-pin-2-line text-sm" />
                  </div>
                  <div>
                    <p className="text-slate-900 font-bold text-sm">{t('contact.map.title')}</p>
                    <p className="text-slate-400 text-xs">{t('contact.map.sub')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#05294A]/10 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#67CBFF] animate-pulse" />
                  <span className="text-[#05294A] text-xs font-semibold">{t('contact.map.active')}</span>
                </div>
              </div>
              <div className="relative">
                <iframe
                  title="FMA Crew Location — Norcross, GA"
                  width="100%"
                  height="280"
                  style={{ border: 0, display: 'block' }}
                  src="https://maps.google.com/maps?q=6500+McDonough+Dr+%23C15%2C+Norcross%2C+GA+30093&output=embed&z=15"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div className="px-4 md:px-5 py-3 border-t border-slate-100">
                <p className="text-slate-400 text-xs mb-2 font-medium uppercase tracking-wide">{t('contact.map.areasLabel')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {['Atlanta Metro', 'North Georgia', 'South Georgia', 'East Georgia', 'West Georgia', 'Coastal Georgia', 'Central Georgia'].map((area) => (
                    <span key={area} className="px-2.5 py-1 bg-[#B8E2E8]/20 border border-[#B8E2E8] text-[#05294A] rounded-full text-xs font-medium">{area}</span>
                  ))}
                  <span className="px-2.5 py-1 bg-[#05294A] border border-[#05294A] text-white rounded-full text-xs font-bold">GA Statewide</span>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <form
              data-readdy-form
              id="contact-form"
              onSubmit={handleSubmit}
              className="bg-white border border-slate-200 p-5 md:p-8 flex flex-col gap-4 md:gap-5 rounded-xl"
            >
              <div className="flex flex-wrap items-start md:items-center justify-between gap-3 pb-2 border-b border-slate-100">
                <div>
                  <p className="text-slate-900 font-bold text-base">{t('contact.form.title')}</p>
                  <p className="text-slate-400 text-xs mt-0.5">{t('contact.form.sub')}</p>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-[#B8E2E8]/20 border border-[#B8E2E8]">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#05294A] text-white">
                    <i className="ri-timer-flash-line text-xs" />
                  </div>
                  <div>
                    <p className="text-[#05294A] font-bold text-xs whitespace-nowrap">{t('contact.form.badge')}</p>
                    <p className="text-[#92D7E8] text-xs whitespace-nowrap">{t('contact.form.badgeSub')}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                <div>
                  <label htmlFor="firstName" className="block text-slate-600 text-xs uppercase tracking-wide mb-2 font-medium">{t('contact.form.firstName')}</label>
                  <input id="firstName" name="firstName" type="text" required placeholder={t('contact.form.firstPlaceholder')}
                    className="w-full bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 rounded-none px-4 py-3 text-sm focus:outline-none focus:border-[#05294A] transition-colors duration-200" />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-slate-600 text-xs uppercase tracking-wide mb-2 font-medium">{t('contact.form.lastName')}</label>
                  <input id="lastName" name="lastName" type="text" required placeholder={t('contact.form.lastPlaceholder')}
                    className="w-full bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 rounded-none px-4 py-3 text-sm focus:outline-none focus:border-[#05294A] transition-colors duration-200" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                <div>
                  <label htmlFor="email" className="block text-slate-600 text-xs uppercase tracking-wide mb-2 font-medium">{t('contact.form.email')}</label>
                  <input id="email" name="email" type="email" required placeholder="john@company.com"
                    className="w-full bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 rounded-none px-4 py-3 text-sm focus:outline-none focus:border-[#05294A] transition-colors duration-200" />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-slate-600 text-xs uppercase tracking-wide mb-2 font-medium">{t('contact.form.phone')}</label>
                  <input id="phone" name="phone" type="tel" placeholder="(555) 000-0000"
                    className="w-full bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 rounded-none px-4 py-3 text-sm focus:outline-none focus:border-[#05294A] transition-colors duration-200" />
                </div>
              </div>

              <div>
                <label htmlFor="service" className="block text-slate-600 text-xs uppercase tracking-wide mb-2 font-medium">{t('contact.form.service')}</label>
                <select id="service" name="service" required
                  className="w-full bg-slate-50 border border-slate-200 text-slate-800 rounded-none px-4 py-3 text-sm focus:outline-none focus:border-[#05294A] transition-colors duration-200 cursor-pointer">
                  <option value="">{t('contact.form.serviceDefault')}</option>
                  <option value="Apartment Management">{t('contact.form.svc1')}</option>
                  <option value="Residential Remodeling">{t('contact.form.svc2')}</option>
                  <option value="Commercial Remodeling">{t('contact.form.svc3')}</option>
                  <option value="General Maintenance">{t('contact.form.svc4')}</option>
                  <option value="Tenant Improvements">{t('contact.form.svc5')}</option>
                  <option value="Other">{t('contact.form.svc6')}</option>
                </select>
              </div>

              <div>
                <label htmlFor="property" className="block text-slate-600 text-xs uppercase tracking-wide mb-2 font-medium">{t('contact.form.property')}</label>
                <input id="property" name="property" type="text" placeholder={t('contact.form.propertyPlaceholder')}
                  className="w-full bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 rounded-none px-4 py-3 text-sm focus:outline-none focus:border-[#05294A] transition-colors duration-200" />
              </div>

              <div>
                <label htmlFor="message" className="block text-slate-600 text-xs uppercase tracking-wide mb-2 font-medium">
                  {t('contact.form.message')}
                  <span className={`ml-2 font-normal ${charCount > 450 ? 'text-red-500' : 'text-slate-400'}`}>({charCount}/500)</span>
                </label>
                <textarea id="message" name="message" required rows={4} maxLength={500}
                  placeholder={t('contact.form.messagePlaceholder')}
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 rounded-none px-4 py-3 text-sm focus:outline-none focus:border-[#05294A] transition-colors duration-200 resize-none" />
              </div>

              <button type="submit" disabled={status === 'loading' || charCount > 500}
                className="w-full py-3.5 bg-[#05294A] text-white rounded-md font-semibold text-sm hover:bg-[#032240] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap">
                {status === 'loading' ? (
                  <><i className="ri-loader-4-line animate-spin" />{t('contact.form.submitting')}</>
                ) : (
                  <><span>{t('contact.form.submit')}</span><i className="ri-send-plane-fill" /></>
                )}
              </button>

              <p className="text-center text-slate-400 text-xs flex items-center justify-center gap-1.5">
                <i className="ri-lock-line" />{t('contact.form.privacy')}
              </p>

              {status === 'success' && (
                <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200">
                  <i className="ri-checkbox-circle-fill text-green-500 text-lg" />
                  <p className="text-green-700 text-sm">{t('contact.form.success')}</p>
                </div>
              )}
              {status === 'error' && (
                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200">
                  <i className="ri-error-warning-fill text-red-500 text-lg" />
                  <p className="text-red-700 text-sm">{t('contact.form.error')}</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
